import { createContext, useContext } from 'react'

import type {
  ApiClient,
  SessionManager,
  TokenManager,
} from 'clients/yogi-api/types'

/**
 * The full client type — the ApiClientProvider requires an object that
 * implements all three ports so every consumer hook can narrow to just
 * the slice it needs (Interface Segregation Principle).
 */
export type YogiApiClientInstance = ApiClient & TokenManager & SessionManager

export const ApiClientContext = createContext<YogiApiClientInstance | null>(
  null,
)

function useYogiApiContext(): YogiApiClientInstance {
  const client = useContext(ApiClientContext)
  if (!client) {
    throw new Error(
      'useApiClient / useTokenManager / useSessionManager must be used within an <ApiClientProvider>',
    )
  }
  return client
}

/** Used by service classes (via their `useXService()` hooks) to make HTTP calls. */
export function useApiClient(): ApiClient {
  return useYogiApiContext()
}

/** Used by AuthContainer to manage auth/shared tokens. */
export function useTokenManager(): TokenManager {
  return useYogiApiContext()
}

/** Used by AuthContainer to manage session-expiry state. */
export function useSessionManager(): SessionManager {
  return useYogiApiContext()
}
